import { ThemeProvider } from "@sunday/component";
import { useTranslation } from "next-i18next";
import { ICTH_THEME } from "src/components/Common/ICTHLayout/constants";
import defaultRefundPolicyContent from "../../../../public/static/locales/en/refund-policy.json";
import {
  RefundPolicyArticle,
  RefundPolicyBodyText,
  RefundPolicyContent,
  RefundPolicyContentContainer,
  RefundPolicyHeading,
  RefundPolicyLink,
  RefundPolicyList,
  RefundPolicyListItem,
  RefundPolicyPageContainer,
  RefundPolicyParagraph,
  RefundPolicySectionContainer,
  RefundPolicySectionTitle,
  RefundPolicyTitle,
} from "./RefundPolicy.style";

type RefundPolicySection = {
  title: string;
  description: string;
};

type RefundPolicyContact = {
  prefix: string;
  link: string;
  suffix: string;
};

type RefundPolicyRemark = {
  title: string;
  items: string[];
};

const isTranslationObject = <T,>(value: unknown): value is T =>
  typeof value === "object" && value !== null;

const RefundPolicy = (): JSX.Element => {
  const { i18n, t } = useTranslation("refund-policy");
  const translatedSections = t("sections", { returnObjects: true });
  const translatedContact = t("contact", { returnObjects: true });
  const translatedRemark = t("remark", { returnObjects: true });
  const sections = Array.isArray(translatedSections)
    ? (translatedSections as RefundPolicySection[])
    : defaultRefundPolicyContent.sections;
  const contact = isTranslationObject<RefundPolicyContact>(translatedContact)
    ? translatedContact
    : defaultRefundPolicyContent.contact;
  const remark = isTranslationObject<RefundPolicyRemark>(translatedRemark)
    ? translatedRemark
    : defaultRefundPolicyContent.remark;
  const locale = i18n.language === "th" ? "th" : "en";

  return (
    <ThemeProvider theme={ICTH_THEME}>
      <RefundPolicyPageContainer>
        <RefundPolicyContentContainer>
          <RefundPolicyContent>
            <RefundPolicyTitle lang={locale}>
              {t("title", {
                defaultValue: defaultRefundPolicyContent.title,
              })}
            </RefundPolicyTitle>
            <RefundPolicyArticle>
              {sections.map(({ title, description }) => (
                <RefundPolicySectionContainer key={title}>
                  <RefundPolicyHeading>
                    <RefundPolicySectionTitle>{title}</RefundPolicySectionTitle>
                  </RefundPolicyHeading>
                  <RefundPolicyParagraph>
                    <RefundPolicyBodyText>{description}</RefundPolicyBodyText>
                  </RefundPolicyParagraph>
                </RefundPolicySectionContainer>
              ))}

              <RefundPolicyParagraph data-contact="true">
                <RefundPolicyBodyText>
                  {contact.prefix}
                  <RefundPolicyLink href={`/${locale}/contact-us`}>
                    {contact.link}
                  </RefundPolicyLink>
                  {contact.suffix}
                </RefundPolicyBodyText>
              </RefundPolicyParagraph>

              <RefundPolicyHeading>
                <RefundPolicySectionTitle>
                  {remark.title}
                </RefundPolicySectionTitle>
              </RefundPolicyHeading>
              <RefundPolicyList>
                {remark.items.map((item) => (
                  <RefundPolicyListItem key={item}>
                    <RefundPolicyBodyText>{item}</RefundPolicyBodyText>
                  </RefundPolicyListItem>
                ))}
              </RefundPolicyList>
            </RefundPolicyArticle>
          </RefundPolicyContent>
        </RefundPolicyContentContainer>
      </RefundPolicyPageContainer>
    </ThemeProvider>
  );
};

export default RefundPolicy;
